import dagre from "@dagrejs/dagre";
import type { ExplorerNode, ExplorerEdge } from "../../declarations/IExplorerTypes";

function computeNodeHeight(node: ExplorerNode, defaultHeight: number): number {
    if (node.type === "salesOrderLinesNode") {
        const lineCount = node.data.lines?.length ?? 0;
        return Math.max(120, 66 + lineCount * 40);
    }
    if (node.type === "serviceOrderLinesNode") {
        const groups = node.data.lineGroups ?? [];
        const expandedItemCount = groups.reduce(
            (acc, g) => acc + (g.isExpanded ? g.items.length : 0),
            0,
        );
        return Math.max(120, 66 + groups.length * 40 + expandedItemCount * 28);
    }
    if (node.type === "customerPaymentsNode") {
        const paymentCount = node.data.payments?.length ?? 0;
        if (paymentCount === 0) {
            return 120;
        }
        return Math.max(120, 66 + 28 + paymentCount * 42);
    }
    if (node.type === "lineDetailNode") {
        return 200;
    }
    return defaultHeight;
}

interface ILayoutOptions {
    direction?: "LR" | "TB";
    nodeWidth?: number;
    nodeHeight?: number;
    rankSep?: number;
    nodeSep?: number;
}

export function applyDagreLayout(
    nodes: ExplorerNode[],
    edges: ExplorerEdge[],
    options?: ILayoutOptions,
): { nodes: ExplorerNode[]; edges: ExplorerEdge[] } {
    const {
        direction = "LR",
        nodeWidth = 280,
        nodeHeight = 120,
        rankSep = 80,
        nodeSep = 40,
    } = options ?? {};

    // Separate FM Route nodes — they're positioned manually below their parent
    const dagreNodes = nodes.filter((n) => n.type !== "fmRouteNode");
    const fmNodes = nodes.filter((n) => n.type === "fmRouteNode");
    const dagreEdges = edges.filter((e) => !e.id.startsWith("e-fmroute-"));

    const g = new dagre.graphlib.Graph();
    g.setDefaultEdgeLabel(() => ({}));
    g.setGraph({
        rankdir: direction,
        ranksep: rankSep,
        nodesep: nodeSep,
    });

    for (const node of dagreNodes) {
        const height = computeNodeHeight(node, nodeHeight);
        g.setNode(node.id, { width: nodeWidth, height });
    }

    for (const edge of dagreEdges) {
        g.setEdge(edge.source, edge.target);
    }

    dagre.layout(g);

    const layoutedNodes = dagreNodes.map((node) => {
        const nodeWithPosition = g.node(node.id);
        const height = computeNodeHeight(node, nodeHeight);
        return {
            ...node,
            position: {
                x: nodeWithPosition.x - nodeWidth / 2,
                y: nodeWithPosition.y - height / 2,
            },
        };
    });

    // Position FM Route nodes below their parent line detail
    const nodeMap = new Map(layoutedNodes.map((n) => [n.id, n]));
    const fmOffset = 80; // gap between bottom of parent and top of FM node

    for (const fmNode of fmNodes) {
        const fmEdge = edges.find((e) => e.target === fmNode.id && e.id.startsWith("e-fmroute-"));
        const parent = fmEdge ? nodeMap.get(fmEdge.source) : undefined;
        if (parent) {
            const parentHeight = computeNodeHeight(parent, nodeHeight);
            layoutedNodes.push({
                ...fmNode,
                position: {
                    x: parent.position.x,
                    y: parent.position.y + parentHeight + fmOffset,
                },
            });
        } else {
            layoutedNodes.push(fmNode);
        }
    }

    return { nodes: layoutedNodes, edges };
}
